import {Bar, NewBarSubmission, sortedDeals} from "../../interfaces/Bar";
import {IDeal, INewDeal, NewDealSuggestion} from "../../interfaces/IDeal";
import {groupConsecutiveDaysByWorkingHours, Schedule, WorkingHoursMap} from "../../tools/groupHours";
import {isEqual} from "lodash";
import omitKeys from "../../tools/omitKeys";

export function areDealListsEqual<O extends IDeal, S extends NewDealSuggestion>(original: O[], current: S[]) {
    const deals1 = sortedDeals(original)
    const deals2 = sortedDeals(current)
    return deals1.length == deals2.length && (
        deals1.every((deal1,index) => areDealsEqual(deal1, deals2[index]))
    )
}

export function areBarsEqual(original: Bar, current: NewBarSubmission) {
    const newBar = {
        location: current.location,
        description: current.description,
        id: current.maps_id,
        chain: current.chain ? {
            ...omitKeys(current.chain, ["original"]),
            updated_at: original.chain?.updated_at ?? null,
        } : undefined,
        name: current.name,
        address: current.address,
        updated_at: original.updated_at,
    } as Bar
    return isEqual(original, newBar)
}

function areDealsEqual(original: INewDeal, current: INewDeal) {
    return original.description == current.description && original.conditions == current.conditions && (
        calculateScheduleDiff(original.days, current.days).length == 0
    )
}

export interface ScheduleDiff {
    schedule: Schedule,
    added: boolean,
    removed: boolean,
    changedHoursOriginalHours: number[] | undefined
}

function arraysEqual(array1: any[], array2: any[]): boolean {
    return array1.length === array2.length && array1.every((value, index) => value === array2[index])
}

export function calculateScheduleDiff(original: WorkingHoursMap | undefined, current: WorkingHoursMap, includeUnchanged: boolean = false): ScheduleDiff[] {
    const schedulesOriginal = original ? groupConsecutiveDaysByWorkingHours(original) : []
    const schedulesCurrent = groupConsecutiveDaysByWorkingHours(current)

    const removed = schedulesOriginal.filter(schedule1 => !schedulesCurrent.find(schedule2 => arraysEqual(schedule2.days, schedule1.days)))
        .map(value => {
            return {
                schedule: value,
                removed: true,
                added: false
            } as ScheduleDiff
        })
    const added = schedulesCurrent.filter(schedule2 => !schedulesOriginal.find(schedule1 => arraysEqual(schedule1.days, schedule2.days)))
        .map(value => {
            return {
                schedule: value,
                removed: false,
                added: true
            } as ScheduleDiff
        })
    const others = schedulesCurrent
        .filter(schedule2 => !added.find(a => a.schedule == schedule2))
        .map(schedule2 => {
            const oldCounterpart = schedulesOriginal.find(schedule1 => arraysEqual(schedule1.days, schedule2.days))
            if (!arraysEqual(oldCounterpart?.hours ?? [], schedule2.hours)) {
                return {
                    schedule: schedule2,
                    added: false,
                    removed: false,
                    changedHoursOriginalHours: oldCounterpart?.hours
                } as ScheduleDiff
            }
            else return includeUnchanged ? {
                schedule: schedule2,
                added: false,
                removed: false
            } as ScheduleDiff : undefined
        }).filter(a => a !== undefined)

    return [...removed, ...added, ...others]
}